import React, { useState, ReactNode, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCouple } from '../../contexts/CoupleContext';
import {
  Gift,
  CheckSquare,
  DollarSign,
  Calendar,
  Camera,
  Users,
  Settings,
  Menu,
  X,
  LogOut,
  Home,
  ChevronDown,
  CreditCard,
} from 'react-feather';
import { Couple } from '../../types';
import BalanceService from '../../services/balance.service';
import { formatPriceSimple, SupportedCurrency } from '../../utils/currency';
import logo from '../../asset/new.png'; // Import the logo

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  id: string;
  href: string;
  icon: React.ElementType;
  label: string;
  highlight?: boolean;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const { currentUser, logout } = useAuth();
  const { couples, currentCouple, selectCouple } = useCouple();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Balance state
  const [accountBalance, setAccountBalance] = useState<number>(0);
  const [balanceCurrency, setBalanceCurrency] = useState<string>('NGN');
  const [loadingBalance, setLoadingBalance] = useState<boolean>(true);

  // Fetch user's balance when the component mounts
  useEffect(() => {
    const fetchBalance = async () => {
      if (currentUser) {
        try {
          setLoadingBalance(true);
          const balanceData = await BalanceService.getUserBalance();
          setAccountBalance(balanceData.accountBalance);
          setBalanceCurrency(balanceData.currency || 'NGN');
        } catch (error) {
          console.error('Error fetching account balance:', error);
        } finally {
          setLoadingBalance(false);
        }
      }
    };

    fetchBalance();
    // Set up a refresh interval (every 5 minutes)
    const interval = setInterval(fetchBalance, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [currentUser]);

  const navigation: NavigationItem[] = [
    { id: 'dashboard', href: '/admin', icon: Home, label: 'Dashboard' },
    { id: 'todos', href: '/admin/todos', icon: CheckSquare, label: 'Wedding To-Do' },
    { id: 'registry', href: '/admin/registry', icon: Gift, label: 'Gift Registry' },
    { id: 'wedding-fund', href: '/admin/wedding-fund', icon: DollarSign, label: 'Wedding Fund' },
    { id: 'balance', href: '/admin/balance', icon: CreditCard, label: 'Account Balance', highlight: true },
    { id: 'events', href: '/admin/events', icon: Calendar, label: 'Events' },
    { id: 'photos', href: '/admin/photos', icon: Camera, label: 'Photos' },
    { id: 'guests', href: '/admin/guests', icon: Users, label: 'Guest List' },
    { id: 'settings', href: '/admin/couple-settings', icon: Settings, label: 'Settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelectCouple = (couple: Couple) => {
    selectCouple(couple);
    setDropdownOpen(false);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} md:hidden fixed inset-0 flex z-40`}>
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        ></div>

        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <X className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <div className="flex items-center">
                <img src={logo} alt="TaleOfTwo" className="h-10 w-auto" />
              </div>
            </div>

            {currentCouple && (
              <div className="mt-5 px-4">
                <div className="bg-pink-50 rounded-lg p-4">
                  <h3 className="font-medium text-pink-700">{currentCouple.names}</h3>
                  <p className="text-sm text-pink-500">
                    {new Date(currentCouple.weddingDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}

            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`${
                    location.pathname === item.href
                      ? 'bg-pink-50 text-pink-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${
                    item.highlight ? 'border-l-4 border-pink-500' : ''
                  } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                >
                  <item.icon
                    className={`${
                      location.pathname === item.href
                        ? 'text-pink-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    } mr-4 h-6 w-6`}
                  />
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Account Balance Widget (Mobile) */}
          {currentUser && (
            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200">
              <div className="p-3 bg-pink-50 rounded-lg">
                <div className="flex items-center">
                  <CreditCard className="h-5 w-5 text-pink-500" />
                  <h3 className="ml-2 text-sm font-medium text-pink-700">Account Balance</h3>
                </div>
                <div className="mt-2">
                  {loadingBalance ? (
                    <div className="animate-pulse h-6 w-24 bg-pink-200 rounded"></div>
                  ) : (
                    <div className="text-xl font-semibold text-pink-600">
                      {formatPriceSimple(accountBalance, balanceCurrency as SupportedCurrency)}
                    </div>
                  )}
                  <Link to="/admin/balance" className="mt-1 inline-block text-xs text-pink-500">
                    View transaction history →
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <button
              onClick={handleLogout}
              className="flex-shrink-0 group block w-full flex items-center"
            >
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  {currentUser?.firstName} {currentUser?.lastName}
                </p>
                <div className="flex items-center text-sm text-gray-500 group-hover:text-gray-700">
                  <LogOut className="mr-1 h-4 w-4" />
                  Sign out
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <img src={logo} alt="TaleOfTwo" className="h-10 w-auto" />
              </div>

              {couples.length > 0 && (
                <div className="mt-5 px-4 relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="bg-pink-50 rounded-lg p-4 w-full text-left"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium text-pink-700">
                          {currentCouple ? currentCouple.names : 'Select a couple'}
                        </h3>
                        {currentCouple && (
                          <p className="text-sm text-pink-500">
                            {new Date(currentCouple.weddingDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        )}
                      </div>
                      <ChevronDown className="h-5 w-5 text-pink-500" />
                    </div>
                  </button>

                  {dropdownOpen && (
                    <div className="origin-top-right absolute right-4 left-4 mt-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        {couples.map((couple: Couple) => (
                          <button
                            key={couple._id}
                            onClick={() => handleSelectCouple(couple)}
                            className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            role="menuitem"
                          >
                            {couple.names}
                          </button>
                        ))}
                        <Link
                          to="/admin/create-couple"
                          className="block px-4 py-2 text-sm text-pink-600 hover:bg-gray-100 border-t"
                          role="menuitem"
                        >
                          + Create New Couple
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.id}
                    to={item.href}
                    className={`${
                      location.pathname === item.href
                        ? 'bg-pink-50 text-pink-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } ${
                      item.highlight ? 'border-l-4 border-pink-500' : ''
                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                  >
                    <item.icon
                      className={`${
                        location.pathname === item.href
                          ? 'text-pink-500'
                          : 'text-gray-400 group-hover:text-gray-500'
                      } mr-3 h-5 w-5`}
                    />
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Account Balance Widget (Desktop) */}
            {currentUser && (
              <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200">
                <div className="p-3 bg-pink-50 rounded-lg">
                  <div className="flex items-center">
                    <CreditCard className="h-5 w-5 text-pink-500" />
                    <h3 className="ml-2 text-sm font-medium text-pink-700">Account Balance</h3>
                  </div>
                  <div className="mt-2">
                    {loadingBalance ? (
                      <div className="animate-pulse h-6 w-24 bg-pink-200 rounded"></div>
                    ) : (
                      <div className="text-xl font-semibold text-pink-600">
                        {formatPriceSimple(accountBalance, balanceCurrency as SupportedCurrency)}
                      </div>
                    )}
                    <Link to="/admin/balance" className="mt-1 inline-block text-xs text-pink-500">
                      View transaction history →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <button
                onClick={handleLogout}
                className="flex-shrink-0 w-full group block"
              >
                <div className="flex items-center">
                  <div>
                    <div className="h-9 w-9 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold">
                      {currentUser?.firstName.charAt(0)}{currentUser?.lastName.charAt(0)}
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                      {currentUser?.firstName} {currentUser?.lastName}
                    </p>
                    <div className="flex items-center text-sm text-gray-500 group-hover:text-gray-700">
                      <LogOut className="mr-1 h-4 w-4" />
                      Sign out
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
          <button
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pink-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-6 w-6" />
          </button>
        </div>

        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;